import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    inject,
    OnDestroy,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";

import { BehaviorSubject, combineLatest, map, Observable, Subject, switchMap } from "rxjs";
import { filter, takeUntil, tap } from "rxjs/operators";

import { LgChartsModule } from "@logex/framework/lg-charts";
import {
    LgLocalizationModule,
    useTranslationNamespace,
    LgTranslateService,
} from "@logex/framework/lg-localization";
import { UiCoreModule, IDropdownDefinition } from "@logex/framework/ui-core";
import { IColumnFilterDictionary, IFilterOption } from "@logex/framework/types";

import { IOverlayPopup } from "@codman/shared/ui-overlay";
import {
    FeatureBenchmarkingService,
    IBenchmarkDimension,
    IComparisonType,
    IConnectProvider,
    IMultiDataSelection,
    Provider,
} from "@codman/shared/feature-benchmarking";
import { CodmanUserAuthorizationService } from "@codman/shared/data-access-authorization";

import {
    DataAccessApiService,
    DataAccessStateService,
    FilterCounts,
} from "@codman/exploration/data-access-base";

import {
    IComparisonPickerSource,
    maxAllowedDataSourceCount,
    minAllowedDataSourceCount,
} from "../../comparison-picker/comparison-picker.component";
import { SharedComparisonPickerButtonInfoComponent } from "../../comparison-picker-info/comparison-picker-button-info/comparison-picker-button-info.component";
import { ComparisonPopupInfoClickEvent } from "../../comparison-picker-info/comparison-picker-popup-info/comparison-picker-popup-info.component";

export interface ComparisonPopupEditInitProps {
    allSources: IComparisonPickerSource[];
    selectedProviders: Provider[];
    selectedProvidersMultiData: IMultiDataSelection | null | undefined;
    comparisonType: IComparisonType | null;
    selectedConnectProviders: number[];
    availableConnectProviders: IConnectProvider[];
    benchmarkDimension: IBenchmarkDimension | null | undefined;
}

export type ComparisonPopupEditClickEventUpdate = {
    type: "update";
    selectedProviders: Provider[];
};
export type ComparisonPopupEditClickEventReset = { type: "reset" };
export type ComparisonPopupEditClickEventConnect = {
    type: "connect";
    connectProviders: number[];
    multiData?: boolean;
};
export type ComparisonPopupEditClickEventMultiData = {
    type: "updateMultiData";
    selectedProvider: Provider;
    selectedDimensions: string[];
    connectProviders?: number[];
};

export type ComparisonPopupEditClickEvent =
    | ComparisonPopupEditClickEventUpdate
    | ComparisonPopupEditClickEventReset
    | ComparisonPopupEditClickEventConnect
    | ComparisonPopupEditClickEventMultiData;

interface IComparisonPickerSourceWithState extends IComparisonPickerSource {
    picked: boolean;
}

interface IDimension {
    name: string;
    picked: boolean;
    count: number;
}

@Component({
    selector: "codman-comparison-picker-popup-edit",
    templateUrl: "./comparison-picker-popup-edit.component.html",
    styleUrls: ["./comparison-picker-popup-edit.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        CommonModule,
        UiCoreModule,
        LgChartsModule,
        LgLocalizationModule,
        SharedComparisonPickerButtonInfoComponent,
        FormsModule,
    ],
    providers: [useTranslationNamespace("APP._Shared.ComparisonPicker")],
})
export class SharedComparisonPopupEditComponent
    implements IOverlayPopup<ComparisonPopupEditInitProps, ComparisonPopupEditClickEvent>, OnDestroy
{
    private _lgTranslateService = inject(LgTranslateService);
    private _dataAccessApiService = inject(DataAccessApiService);
    private _dataAccessStateService = inject(DataAccessStateService);
    private _benchmarkingService = inject(FeatureBenchmarkingService);
    private _authorizationService = inject(CodmanUserAuthorizationService);
    private _changeDetectorRef = inject(ChangeDetectorRef);

    _comparisonType: IComparisonType = "basic";
    readonly minAllowedDataSourceCount = minAllowedDataSourceCount;
    readonly maxAllowedDataSourceCount = maxAllowedDataSourceCount;

    // basic type data
    _allSources: IComparisonPickerSourceWithState[] = [];
    _selectedConnectProviders: number[] = [];
    _availableConnectProviders: IConnectProvider[] = [];
    _currentConnectProviders: number[] = [];
    _pickedCount = 0;
    _pickedDimensionsCount = 0;
    _pickedDimensions: { [key: string]: number } = {};

    // multiData type data
    _allDimensions: IDimension[] = [];
    _filteredDimensions: IDimension[] = [];
    _currentProvider: Provider = "ownProvider";
    _currentSearchFilter = "";
    _providersDefinition: IDropdownDefinition<string> = {
        groups: [],
    };
    _connectsDefinition: IFilterOption[] = [];
    _currentSearchFilterConnect = "";
    _currentConnectProvidersInMultiData: IColumnFilterDictionary = <IColumnFilterDictionary>{
        $empty: true,
    };
    _connectsSelectedCount = 0;

    _currentProvider$: BehaviorSubject<Provider> = new BehaviorSubject<Provider>("ownProvider");
    private _destroy$ = new Subject<void>();

    _dimensionsLoading = false;
    _treatmentComparisonAllowed = false;

    readonly _clickEvent$ = new Subject<ComparisonPopupEditClickEvent>();

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    initialize(config: ComparisonPopupEditInitProps): Observable<ComparisonPopupEditClickEvent> {
        this._comparisonType = config.comparisonType ?? "basic";
        this._treatmentComparisonAllowed = config.benchmarkDimension?.outcomeCode !== undefined;

        this._initializeBasicTypeData(config);
        this._initializeMultiDataTypeData(config);

        combineLatest([
            this._dataAccessStateService.fullNavigationState$,
            this._benchmarkingService.benchmarkDimension$,
            this._authorizationService.organizationId$,
            this._dataAccessStateService.benchmarkApiParams$,
            this._currentProvider$,
        ])
            .pipe(
                takeUntil(this._destroy$),
                filter(
                    ([{ registryId, subsetId }]) => registryId.length > 0 && subsetId.length > 0,
                ),
                tap(() => (this._dimensionsLoading = true)),
                switchMap(
                    ([
                        { registryId, subsetId },
                        benchmarkDimension,
                        organizationId,
                        benchmarkApiParams,
                        currentProvider,
                    ]) => {
                        return this._dataAccessApiService
                            .getFilterCounts(
                                registryId,
                                subsetId,
                                benchmarkDimension?.outcomeCode ?? null,
                                organizationId,
                                currentProvider,
                                "multiData",
                                this._dataAccessStateService.getBenchmarkApiParams(
                                    benchmarkApiParams,
                                    currentProvider,
                                    Object.keys(this._currentConnectProvidersInMultiData).map(key =>
                                        parseInt(key),
                                    ),
                                ),
                            )
                            .pipe(
                                map((filterCounts: FilterCounts) => {
                                    let dimensions: IDimension[] = [];
                                    for (const [key, value] of Object.entries(filterCounts)) {
                                        dimensions.push({
                                            name: key,
                                            count: value,
                                            picked: false,
                                        });
                                    }
                                    return dimensions;
                                }),
                            );
                    },
                ),
                tap(() => (this._dimensionsLoading = false)),
                tap(dimensions => {
                    this._allDimensions =
                        dimensions.map(dimension => ({
                            name: dimension.name,
                            picked: false,
                            count: dimension.count,
                        })) ?? [];
                    config.selectedProvidersMultiData?.dimensions.forEach(element => {
                        const item = this._allDimensions.find(
                            dimension => dimension.name === element,
                        );
                        if (item !== undefined) item.picked = true;
                    });
                    this._filterDimensions();
                    this._updatePickedDimensionsCount();
                    this._changeDetectorRef.markForCheck();
                }),
            )
            .subscribe();

        return this._clickEvent$.asObservable();
    }

    _initializeBasicTypeData(config: ComparisonPopupEditInitProps): void {
        // normal providers
        this._allSources = config.allSources.map(source => ({
            ...source,
            picked: config.selectedProviders.includes(source.type),
        }));
        this._updatePickedSourcesCount();

        // connect provider
        this._selectedConnectProviders = config.selectedConnectProviders;
        this._availableConnectProviders = config.availableConnectProviders;
        this._currentConnectProviders = [...this._selectedConnectProviders];
    }

    _initializeMultiDataTypeData(config: ComparisonPopupEditInitProps): void {
        // normal providers
        this._providersDefinition.groups = config.allSources.map(source => ({
            id: source.type,
            name: this._lgTranslateService.translate(
                source.type === "connectBenchmark" ? ".Connect.Title" : (source.text ?? ""),
            ),
        }));
        if (config.selectedProvidersMultiData?.provider)
            this._currentProvider = config.selectedProvidersMultiData?.provider;

        // connect provider
        this._connectsDefinition = config.availableConnectProviders.map(provider => ({
            id: provider.id,
            name: provider.name,
        }));

        this._currentConnectProvidersInMultiData = this._connectsDefinition
            .map(provider => ({
                [provider.id]: provider.name,
            }))
            .reduce((acc, val) => ({ ...acc, ...val }), {});
        if (this._comparisonType === "multiData" || this._selectedConnectProviders.length === 0) {
            this._currentConnectProvidersInMultiData = this._connectsDefinition
                .filter(
                    connect =>
                        (config.selectedProvidersMultiData?.connectProviders.length ?? 0) === 0 ||
                        config.selectedProvidersMultiData?.connectProviders?.includes(
                            Number(connect.id),
                        ),
                )
                .map(provider => ({
                    [provider.id]: provider.name,
                }))
                .reduce((acc, val) => ({ ...acc, ...val }), {});
        } else {
            this._currentConnectProvidersInMultiData = this._selectedConnectProviders
                .map(provider => ({
                    [provider]: this._connectsDefinition.find(connect => connect.id === provider)!
                        .name,
                }))
                .reduce((acc, val) => ({ ...acc, ...val }), {});
        }

        this._updateConnectSelectedCount();
    }

    _updatePosition(): void {
        //
    }

    _onReset(): void {
        this._clickEvent$.next({ type: "reset" });
        this._clickEvent$.complete();
    }

    _onUpdate(): void {
        if (this._comparisonType === "basic") {
            this._clickEvent$.next({
                type: "connect",
                connectProviders: this._currentConnectProviders,
            });
            this._clickEvent$.next({
                type: "update",
                selectedProviders: this._allSources
                    .filter(source => source.picked)
                    .map(source => source.type),
            });
        } else {
            const connectProviders = [];
            for (var key in this._currentConnectProvidersInMultiData) {
                connectProviders.push(parseInt(key));
            }
            this._clickEvent$.next({
                type: "updateMultiData",
                selectedProvider: this._currentProvider,
                selectedDimensions: this._allDimensions
                    .filter(dimension => dimension.picked)
                    .map(dimension => dimension.name),
                connectProviders:
                    this._currentProvider === "connectBenchmark" ? connectProviders : [],
            });
        }
        this._clickEvent$.complete();
    }

    _onCancel(): void {
        this._clickEvent$.complete();
    }

    _onSourceChanged(item: string): void {
        this._allSources.find(source => source.type === item)!.picked = !this._allSources.find(
            source => source.type === item,
        )!.picked;

        this._updatePickedSourcesCount();
        if (
            item === "connectBenchmark" &&
            this._allSources.find(source => source.type === item)!.picked
        ) {
            this._currentConnectProviders = [
                ...this._availableConnectProviders.map(provider => provider.id),
            ];
            this._selectedConnectProviders = this._currentConnectProviders;
        }
    }

    private _updatePickedSourcesCount(): void {
        this._pickedCount = this._allSources.filter(source => source.picked).length;
    }

    _onDimensionChanged(item: string): void {
        this._allDimensions.find(dimension => dimension.name === item)!.picked =
            !this._allDimensions.find(dimension => dimension.name === item)!.picked;

        this._updatePickedDimensionsCount();
    }

    private _updatePickedDimensionsCount(): void {
        let index = 0;
        this._pickedDimensions = this._allDimensions
            .filter(dimension => dimension.picked)
            .reduce(
                (acc, dimension) => {
                    acc[dimension.name] = ++index;
                    return acc;
                },
                {} as { [key: string]: number },
            );
        this._pickedDimensionsCount = index;
    }

    _handlePickerInfoEvent(event: ComparisonPopupInfoClickEvent): void {
        if (event.type === "connect") {
            this._currentConnectProviders = event.connectProviders;
            this._selectedConnectProviders = this._currentConnectProviders;
        }
    }

    _onComparisonTypeChanged(type: IComparisonType): void {
        this._comparisonType = type;

        if (type === "basic") {
            this._updatePickedSourcesCount();
        } else {
            this._updatePickedDimensionsCount();
        }
    }

    _triggerSearchFilterChange(): void {
        this._filterDimensions();
    }

    _filterDimensions(): void {
        this._filteredDimensions = [
            ...this._allDimensions.filter(dimension => dimension.picked === true),
            ...this._allDimensions.filter(
                dimension =>
                    dimension.picked === false &&
                    (!this._currentSearchFilter ||
                        dimension.name
                            .toLowerCase()
                            .includes(this._currentSearchFilter.toLowerCase())),
            ),
        ];
    }

    _triggerConnectChange(): void {
        if (this._currentConnectProvidersInMultiData.$empty)
            this._currentConnectProvidersInMultiData = this._connectsDefinition
                .map(provider => ({
                    [provider.id]: provider.name,
                }))
                .reduce((acc, val) => ({ ...acc, ...val }), {});
        this._updateConnectSelectedCount();
    }

    _updateConnectSelectedCount(): void {
        this._connectsSelectedCount = this._currentConnectProvidersInMultiData.$empty
            ? 0
            : Object.keys(this._currentConnectProvidersInMultiData).length;
    }

    _onProviderChanged(provider: string): void {
        this._currentProvider$.next(provider as Provider);
    }
}
